import {
  Box,
  Button,
  Grid2,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import ContactModal from "../../components/ContactModal";
import ArrowForward from "@mui/icons-material/ArrowForward";

const FirstSection = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-between",
        alignItems: "center",
        height: "62vh",
      }}
    >
      <Box
        sx={{
          width: "45%",
          height: 400,
          backgroundImage: "url(/images/howwework/img_one.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <Box
        sx={{
          width: "50%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: {
              md: "3rem",
              laptop: "3.5rem",
              lg: "4rem",
              xl: "4.25rem",
            },
          }}
          variant="h2"
        >
          Why Chose us?
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontFamily: "'inter', sans-serif",
            fontSize: {
              md: "0.8rem",
              laptop: "0.8rem",
              lg: "1.225rem",
            },
          }}
        >
          At InfinityHyperCloud, we create{" "}
          <span
            style={{
              color: "#fff",
              opacity: 1,
            }}
          >
            powerful, high-performance applications that combine innovation with
            efficiency.
          </span>{" "}
          Whether you're launching a{" "}
          <span
            style={{
              color: "#fff",
              opacity: 1,
            }}
          >
            customer-facing platform, an enterprise tool, or an ecommerce
            solution
          </span>
          , we build future-ready applications designed for success
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontFamily: "'inter', sans-serif",
            // maxWidth: 650,
            fontSize: {
              md: "0.8rem",
              laptop: "0.8rem",
              lg: "1.225rem",
            },
          }}
        >
          <span
            style={{
              color: "#fff",
              opacity: 1,
            }}
          >
            Innovate with confidence—bring your vision to life with
            InfinityHyperCloud
          </span>
        </Typography>
        <Button
          type="submit"
          variant="outlined"
          endIcon={<ArrowForward />}
          href="https://calendar.app.google/yyQ665soutqZzj1H9"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "rgba(255,255,255,0.95)",
            borderColor: "rgba(255,255,255,0.4)",
            px: 3,
            py: 1.25,
            maxWidth: 200,
            textTransform: "none",
            borderRadius: 1,
            "&:hover": { borderColor: "rgba(255,255,255,0.75)" },
          }}
        >
          Schedule a call
        </Button>
      </Box>
    </Box>
  );
};

const processSteps = [
  {
    title: "Business-Driven Development",
    description:
      "We begin by understanding your business objectives, challenges, and technical requirements. Through in-depth research and analysis, we define a clear roadmap to ensure our solutions align with your vision.",
  },
  {
    title: "Design & Development",
    description:
      "Using modern cloud technologies, SaaS architecture, and data analytics frameworks, we build scalable, secure, and efficient digital solutions tailored to your needs. Our development process prioritizes performance, security, and seamless integration.",
  },
  {
    title: "Testing & Optimization",
    description:
      "Before deployment, we rigorously test and optimize our solutions to ensure they meet high-performance standards. We fine-tune every aspect, from speed and security to user experience and scalability.",
  },
  {
    title: "Deployment & Continuous Support",
    description:
      "We ensure a seamless deployment and provide ongoing support, updates, and scalability enhancements to keep your software performing at its best. Our goal is to ensure long-term success and adaptability in an ever-evolving digital landscape.",
  },
];

const ProcessStepCard = ({ title, description }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        padding: 3,
        borderRadius: 2,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        backgroundColor: theme.palette.background.darkBlue,
        gap: 4,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.secondary.white, marginTop: 1 }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const SecondSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // backgroundColor: "#0F0D21
      }}
    >
      <Box sx={{ maxWidth: "1720px" }}>
        <Typography variant="h3" sx={{ color: "#fff" }}>
          Our Process
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 4,
            padding: 2,
          }}
        >
          {processSteps.map((step, index) => (
            <ProcessStepCard
              key={index}
              title={step.title}
              description={step.description}
            />
          ))}
        </Box>

        {/* Footer Text */}
        <Typography
          variant="subtitle2"
          sx={{
            marginTop: 4,
            color: theme.palette.primary.white,
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          From idea to execution—our structured approach delivers solutions that
          scale, perform, and evolve with your business.
        </Typography>
      </Box>
    </Box>
  );
};

const techStack = [
  { name: "React.js", image: "/images/howwework/react.png" },
  { name: "React Native", image: "/images/howwework/react-native.png" },
  { name: "Next.js", image: "/images/howwework/next-js.png" },
  { name: "Vue.js", image: "/images/howwework/vue.png" },
  { name: "Angular.js", image: "/images/howwework/angular.png" },
  { name: "Node.js", image: "/images/howwework/nodejs.png" },
  { name: "Nest.js", image: "/images/howwework/nest.js.png" },
  { name: "Express.js", image: "/images/howwework/express.js.png" },
  { name: "GraphQL", image: "/images/howwework/graphql.png" },
  { name: "PostgreSQL", image: "/images/howwework/postgresql.png" },
  { name: "MySQL", image: "/images/howwework/mysql.png" },
  { name: "MongoDB", image: "/images/howwework/mongodb.png" },
  { name: "Kafka", image: "/images/howwework/kafka.png" },
  { name: "Firebase", image: "/images/howwework/firebase.png" },
  { name: "BigQuery", image: "/images/howwework/bigquery.png" },
  { name: "Snowflake", image: "/images/howwework/snowflake.png" },
  { name: "Azure", image: "/images/howwework/azure.png" },
  { name: "AWS", image: "/images/howwework/aws.png" },
  { name: "Docker", image: "/images/howwework/docker.png" },
  { name: "Supabase", image: "/images/howwework/supabase.png" },
  { name: "Figma", image: "/images/howwework/figma-logo.png" },
];

const TechStackSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        padding: 4,
      }}
    >
      {/* Section Title */}
      <Typography
        variant="h3"
        sx={{
          marginBottom: 4,
        }}
      >
        Our Tech Stack
      </Typography>

      <Grid2 container spacing={2} justifyContent="center">
        {techStack.map((item, index) => (
          <Grid2 item key={index} xs={4} sm={2} md={1.5}>
            <Box
              sx={{
                maxWidth: "230px",
                maxHeight: "230px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                padding: 2,
                backgroundColor: theme.palette.background.darkBlue,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignContent: "center",
                  justifyContent: "center",
                  width: 170,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 120,
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    objectFit: "contain",
                  }}
                />
                {/* <Typography variant="h4" textAlign="center">
                  {item.name}
                </Typography> */}
              </Box>
            </Box>
          </Grid2>
        ))}
      </Grid2>
      <Typography
        variant="subtitle2"
        sx={{
          marginTop: 4,
          color: theme.palette.primary.white,
          textAlign: "center",
          fontStyle: "italic",
        }}
      >
        We use the right technologies to build solutions that drive results and
        scale with your business needs.
      </Typography>
    </Box>
  );
};

const Contact = ({ setOpenModal }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: 4,
        marginBottom: 8,
      }}
    >
      <Box
        sx={{
          width: "90%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 400,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundImage: "url(/images/servicesone/services_bg.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: -1,
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              alignItems: "center",
              gap: 4,
              p: 4,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                marginBottom: 4,
                fontSize: {
                  md: "3rem",
                  laptop: "3.5rem",
                  lg: "4rem",
                  xl: "4.25rem",
                },
              }}
            >
              Lets get in contact
            </Typography>
            <Box
              sx={{
                padding: 1,
              }}
            >
              <Typography variant="h6" textAlign="center">
                We'd love to hear from you and discuss how we can help you
                achieve your business goals. Contact us to learn more about our
                services and get a quote.
              </Typography>
            </Box>
            <Button
              type="submit"
              variant="outlined"
              endIcon={<ArrowForward />}
              href="https://calendar.app.google/yyQ665soutqZzj1H9"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "rgba(255,255,255,0.95)",
                borderColor: "rgba(255,255,255,0.4)",
                px: 3,
                py: 1.25,
                maxWidth: 200,
                textTransform: "none",
                borderRadius: 1,
                "&:hover": { borderColor: "rgba(255,255,255,0.75)" },
              }}
            >
              Schedule a call
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MobileView = () => {
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();

  const displayedItems = showAll ? techStack : techStack.slice(0, 6);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          p: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: "2rem",
              sm: "2.5rem",
              md: "3rem",
            },
          }}
          variant="h2"
        >
          Why Chose us?
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontFamily: "'inter', sans-serif",
            textAlign: "center",
            fontSize: {
              md: "0.8rem",
              laptop: "0.8rem",
              lg: "1.225rem",
            },
          }}
        >
          At InfinityHyperCloud,{" "}
          <span
            style={{
              color: "#fff",
              opacity: 1,
            }}
          >
            powerful, high-performance applications that combine innovation with
            efficiency.
          </span>{" "}
          Whether you're launching a{" "}
          <span
            style={{
              color: "#fff",
              opacity: 1,
            }}
          >
            customer-facing platform, an enterprise tool, or an ecommerce
            solution
          </span>
          , we build future-ready applications designed for success
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "'inter', sans-serif",
            textAlign: "center",
            fontSize: {
              md: "0.8rem",
              laptop: "0.8rem",
              lg: "1.225rem",
            },
          }}
        >
          Innovate with confidence—bring your vision to life with
          InfinityHyperCloud
        </Typography>
        <Button
          href="https://calendar.app.google/yyQ665soutqZzj1H9"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textTransform: "none",
            border: `1px solid ${theme.palette.primary.gray}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "rgba(255,255,255,0.95)",
            borderColor: "rgba(255,255,255,0.4)",
            px: 3,
            py: 1.25,
            ml: 3,
            maxWidth: 200,
            borderRadius: 1,
            "&:hover": { borderColor: "rgba(255,255,255,0.75)" },
          }}
          type="submit"
          variant="outlined"
          endIcon={<ArrowForward />}
        >
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "14px",
            }}
          >
            Schedule a Call
          </Typography>
        </Button>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "450px",
          backgroundImage: "url(/images/howwework/img_one.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* SecondSection */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "95%",
          }}
        >
          <Box sx={{ maxWidth: "1720px" }}>
            <Typography
              variant="h3"
              sx={{ color: "#fff", textAlign: "center" }}
            >
              Our Process
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 4,
                padding: 2,
              }}
            >
              {processSteps.map((step, index) => (
                <ProcessStepCard
                  key={index}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </Box>

            {/* Footer Text */}
            <Typography
              variant="subtitle2"
              sx={{
                marginTop: 4,
                color: theme.palette.primary.white,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              From idea to execution—our structured approach delivers solutions
              that scale, perform, and evolve with your business.
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* TechStackSection */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "95%",
          }}
        >
          {/* Section Title */}
          <Typography
            variant="h3"
            sx={{
              marginBottom: 4,
              textAlign: "center",
            }}
          >
            Our Tech Stack
          </Typography>

          <Box sx={{ position: "relative", mb: 2 }}>
            <Grid2 container spacing={2} padding={2} justifyContent="center">
              {displayedItems.map((item, index) => (
                <Grid2
                  item
                  key={index}
                  backgroundColor={theme.palette.background.darkBlue}
                  alignContent="center"
                  justifyContent="center"
                  size={{ mobile_s: 6, xs: 12, sm: 4 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 120,
                      p: 2,
                    }}
                  >
                    <img
                      width={100}
                      height="auto"
                      src={item.image}
                      alt={item.name}
                    />
                  </Box>
                </Grid2>
              ))}
            </Grid2>

            {!showAll && techStack.length > 6 && (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 100,
                    background:
                      "linear-gradient(180deg, rgba(8,10,29,0) 0%, rgba(8,10,29,1) 100%)",
                    pointerEvents: "none",
                  }}
                />
              </>
            )}
          </Box>
          <Button
            onClick={() => setShowAll(!showAll)}
            variant="outlined"
            sx={{
              alignSelf: "center",
              maxWidth: 220,
              mb: 4,
              width: "100%",
              textTransform: "none",
              borderColor: "rgba(255,255,255,0.4)",
              color: "rgba(255,255,255,0.95)",
              "&:hover": { borderColor: "rgba(255,255,255,0.75)" },
            }}
          >
            {showAll ? "See Less" : "See More"}
          </Button>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: theme.palette.primary.white,
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            We use the right technologies to build solutions that drive results
            and scale with your business needs.
          </Typography>
        </Box>
      </Box>
      {/* Contact Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundImage: "url(/images/servicesone/services_bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          p: 4,
          gap: 4,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: {
              xs: "2rem",
              sm: "2.5rem",
              md: "3rem",
            },
          }}
        >
          Lets get in contact
        </Typography>
        <Box
          sx={{
            padding: 1,
          }}
        >
          <Typography
            variant="h5"
            textAlign="center"
            sx={{
              fontSize: {
                xs: "0.8rem",
                sm: "1rem",
                md: "1.8rem",
              },
            }}
          >
            We'd love to hear from you and discuss how we can help you achieve
            your business goals. Contact us to learn more about our services and
            get a quote.
          </Typography>
        </Box>
        <Button
          href="https://calendar.app.google/yyQ665soutqZzj1H9"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textTransform: "none",
            border: `1px solid ${theme.palette.primary.gray}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "rgba(255,255,255,0.95)",
            borderColor: "rgba(255,255,255,0.4)",
            px: 3,
            py: 1.25,
            ml: 3,
            maxWidth: 200,
            borderRadius: 1,
            "&:hover": { borderColor: "rgba(255,255,255,0.75)" },
          }}
          type="submit"
          variant="outlined"
          endIcon={<ArrowForward />}
        >
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "14px",
            }}
          >
            Schedule a Call
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

const WebView = ({ openModal, setOpenModal }) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "90%",
          }}
        >
          <FirstSection />
          <SecondSection />
          <TechStackSection />
          <Contact setOpenModal={setOpenModal} />
        </Box>
      </Box>
      <ContactModal openModal={openModal} setOpenModal={setOpenModal} />
    </>
  );
};

const WorkPage = () => {
  const theme = useTheme();
  const [openModal, setOpenModal] = useState(false);

  const isMdScreen = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {isMdScreen && (
        <WebView openModal={openModal} setOpenModal={setOpenModal} />
      )}
      {!isMdScreen && <MobileView />}
    </>
  );
};

export default WorkPage;
